import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, ChevronRight, CheckCircle2, FileText, Package, Shield, Megaphone, Zap } from 'lucide-react';

const SwiggyOnboarding = () => {
  const requiredDocuments = [
    'GST Certificate',
    'PAN Card',
    'Trademark Certificate',
    'FSSAI License (for food products)',
    'Bank Account Details'
  ];

  const onboardingSteps = [
    { step: '1', title: 'Registration', description: 'Submit seller registration application' },
    { step: '2', title: 'Documentation', description: 'Upload required documents' },
    { step: '3', title: 'Brand Approval', description: 'Get brand verified and approved' },
    { step: '4', title: 'Catalog Setup', description: 'Create product listings' },
    { step: '5', title: 'Go Live', description: 'Start selling on Swiggy' }
  ];

  const whyChooseUs = [
    { icon: FileText, title: 'Documentation Support', description: 'Complete guidance on paperwork' },
    { icon: Package, title: 'Catalog Setup', description: 'Professional product listings' },
    { icon: Shield, title: 'Fast Approval', description: 'Expedited brand approval' },
    { icon: Megaphone, title: 'Launch Support', description: 'Go-live assistance' }
  ];

  return (
    <div className="min-h-screen pt-32">
      <Helmet>
        <title>Swiggy Seller Onboarding Services | Start Selling on Swiggy</title>
        <meta name="description" content="Complete Swiggy seller onboarding: registration, documentation & brand approval. Launch your products on Swiggy marketplace with expert guidance." />
        <link rel="canonical" href="https://www.obiquistesolutions.com/swiggy-seller-onboarding" />
      </Helmet>
      {/* Breadcrumb */}
      <div className="bg-slate-50 py-4">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center text-sm text-slate-600">
            <Link to="/" className="hover:text-primary">Home</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <Link to="/services" className="hover:text-primary">Services</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-orange-600 font-medium">Swiggy Seller Onboarding</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-orange-50 to-yellow-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Zap className="w-4 h-4 mr-2" />
                Marketplace Onboarding
              </div>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                Swiggy <span className="text-orange-600">Seller Onboarding</span>
              </h1>
              <p className="text-lg md:text-xl text-slate-600 mb-8 leading-relaxed">
                Complete onboarding support to launch your brand on Swiggy marketplace. 
                From registration to going live, we guide you every step.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact?audit=true">
                  <Button className="bg-orange-600 text-white hover:bg-orange-700 px-8 py-6 rounded-full text-base font-semibold">
                    Start Onboarding
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Onboarding Steps */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-center mb-12">
            Onboarding Process
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {onboardingSteps.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-lg border border-slate-200 text-center"
              >
                <div className="w-12 h-12 bg-orange-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="font-heading font-bold text-lg mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Documents & Why Choose Us */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="font-heading text-3xl font-bold mb-6">Documents Required</h2>
              <ul className="space-y-4">
                {requiredDocuments.map((doc, index) => (
                  <li key={index} className="flex items-center bg-white p-4 rounded-lg shadow-sm">
                    <CheckCircle2 className="w-6 h-6 text-green-500 mr-3" />
                    <span className="text-lg text-slate-700">{doc}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="font-heading text-3xl font-bold mb-6">Why Choose Us?</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {whyChooseUs.map((item, index) => (
                  <div key={index} className="bg-white p-6 rounded-xl shadow-sm">
                    <item.icon className="w-10 h-10 text-orange-600 mb-3" />
                    <h3 className="font-bold mb-1">{item.title}</h3>
                    <p className="text-slate-600 text-sm">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-orange-600 to-yellow-500">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Sell on Swiggy?
          </h2>
          <p className="text-orange-100 text-lg mb-8 max-w-2xl mx-auto">
            Get complete onboarding support and launch your brand on Swiggy marketplace.
          </p>
          <Link to="/contact?audit=true">
            <Button className="bg-white text-orange-600 hover:bg-slate-100 px-8 py-6 rounded-full text-base font-semibold">
              Start Onboarding Now
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SwiggyOnboarding;
